import styled from 'styled-components'

import { MAX_APP_WIDTH } from '@/constants/others'
import { zIndexRegistry } from '@/constants/zIndexRegistry'
import { RoundedChip } from '@/ui/atoms/rounded-chip'
import { Spacing } from '@/ui/atoms/spacing'
import { MEDIA_QUERIES } from '@/ui/constants/mediaQueries'

/**
 * VIP page container
 */
export const VipContainer = styled.div(
  ({ theme }) => `
  max-width: ${MAX_APP_WIDTH};
  padding: 0;
  width: 100%;

  ${MEDIA_QUERIES(theme).medium} {
    padding: 0 ${theme.spacing.default};
  }
    
  ${MEDIA_QUERIES(theme).large} {
    padding: 0 ${theme.spacing.husky};
    margin-right: auto;
    margin-left: auto;
  }

  // The side paddings will default to 0 on screens that pass our max with by 4rem
  @media screen and (min-width: 1480px) {
    padding: 0;
  }
`
)

/**
 * @param addPaddingOnMobile Adds spacing around sizes on mobile - most of VIP components don't have this space
 * @param removeTabletPadding Remove space around sides on medium screens to accommodate components
 */
type VipSectionProps = {
  $removePaddingOnMobile?: boolean
  $removeTabletPadding?: boolean
}
export const VipSectionWrapper = styled(Spacing)<VipSectionProps>(
  ({ theme, $removePaddingOnMobile, $removeTabletPadding }) => `
  padding: ${$removePaddingOnMobile ? 0 : `0 ${theme.spacing.default}`};
  
  ${MEDIA_QUERIES(theme).medium} {
    padding: ${$removeTabletPadding ? '0' : `0 ${theme.spacing.large}`};
  }

  ${MEDIA_QUERIES(theme).large} {
    padding: 0;
  }
`
)

export const TopBannerStickyRoot = styled(VipSectionWrapper)<{
  canStickyTopAd: boolean
  isExiting: boolean
  isSellerVip: boolean
}>(
  ({ theme, canStickyTopAd, isExiting, isSellerVip }) => `    
    ${MEDIA_QUERIES(theme).large} {
      ${
        canStickyTopAd
          ? `
        position: sticky;
        z-index: 102;
        pointer-events: none;
        transform: translateY(0);
        transition: transform 1500ms ease, opacity 800ms ease;

        ${
          isSellerVip
            ? `
          top: -276px;
          `
            : `
          top: -40px;
        `
        }
    
        ${
          isExiting
            ? `
        transform: translateY(-100%);
        opacity: 0;
        `
            : ''
        }
    
        & > * {
          pointer-events: auto;
        }
      `
          : ''
      }
    }
`
)

export const TopBannerSlotsWrapper = styled(VipSectionWrapper)<{ canStickyTopAd: boolean }>(
  ({ theme, canStickyTopAd }) => `
    ${MEDIA_QUERIES(theme).large} {
      ${
        canStickyTopAd
          ? `
        pointer-events: none;
      `
          : ''
      }
    }
`
)

export const VipGridLayout = styled.div(
  ({ theme }) => `
  background-color: ${theme.colors.white};
  display: flex;
  flex-direction: column;
  position: relative;

  ${MEDIA_QUERIES(theme).large} {
    display: grid;
    column-gap: ${theme.spacing.xLarge};
    grid-template-columns: minmax(auto, calc(100% - 41rem - ${theme.spacing.husky})) 41rem;

    // Place every item in the left automatically an only the last one in the right
    /* All items go to the first column by default */
    & > div {
      grid-column: 1 / 2; 
    }
  }

  ${MEDIA_QUERIES(theme).xLarge} {
    column-gap: ${theme.spacing.husky};
    grid-template-columns: minmax(auto, calc(100% - 46rem - ${theme.spacing.husky})) 46rem;
  }

`
)

const SideColumnPlacement = styled(VipSectionWrapper).attrs({ as: 'section' })(
  ({ theme }) => `
    ${MEDIA_QUERIES(theme).large} {
      grid-column: 2 / 3;
      grid-row: 1 / 15;
    }
  `
)

export const VipSidebarContainer = styled(SideColumnPlacement)(
  ({ theme }) => `
  height: fit-content;
  
  ${MEDIA_QUERIES(theme).large} {
    display: flex;
    flex-direction: column;
    height: 100%;
    z-index: ${zIndexRegistry.VIP_STICKY_SIDE};
  }
`
)

export const GptBigboxWrapper = styled.div(
  ({ theme }) => `  
  ${MEDIA_QUERIES(theme).large} {
    height: 600px;
    position: sticky;
    top: 0;
  }
`
)

export const VipPromotedUrlsContainer = styled.div(
  ({ theme }) => `
  max-width: ${MAX_APP_WIDTH};
  padding: 0;
  width: 100%;
  padding: 0 ${theme.spacing.default};

  ${MEDIA_QUERIES(theme).medium} {
    padding: 0 ${theme.spacing.default};
  }
    
  ${MEDIA_QUERIES(theme).large} {
    padding: 0 ${theme.spacing.husky};
    margin-right: auto;
    margin-left: auto;
  }

  @media screen and (min-width: 1480px) {
    padding: 0;
  }
`
)

export const VipPromotedUrls = styled.div(
  ({ theme }) => `
    overflow: auto;
    white-space: nowrap;
    margin-block: ${theme.spacing.default};
    padding: 1rem 0.5rem;
    background-color: #F8F9F9;
    text-align: center;
`
)

export const CustomRoundedChip = styled(RoundedChip)`
  margin-inline: 0.5rem;

  &:first-of-type {
    margin-left: 0;
  }

  &:last-of-type {
    margin-right: 0;
  }
`
